import React, { useState, useRef, useEffect } from 'react'

import { Input, Button, type InputRef } from 'antd'
import { EditOutlined, CheckOutlined } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { updateFamilyNameThunk } from 'features/team/model/slice/teamSlice'
import { getUserRole } from 'features/user/model/selectors/userSelectors'
import { MemberRole } from 'features/team/model/types/members'
import { hasAccess } from 'shared/utils/access'
import styled from 'styled-components'

interface EditableTeamNameProps {
  teamName: string
  className?: string
}

const EditableTeamName: React.FC<EditableTeamNameProps> = ({ teamName, className }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState(teamName)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<InputRef>(null)
  const dispatch = useAppDispatch()
  const userRole = useAppSelector(getUserRole)

  const canEdit = hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER])

  useEffect(() => {
    setName(teamName)
  }, [teamName])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (canEdit) {
      setIsEditing(true)
    }
  }

  const handleSave = async () => {
    const trimmedName = name.trim()

    if (!trimmedName) {
      return
    }

    if (trimmedName === teamName) {
      setIsEditing(false)
      return
    }

    try {
      setIsLoading(true)
      await dispatch(updateFamilyNameThunk({ name: trimmedName })).unwrap()
      setIsEditing(false)
    } finally {
      setIsLoading(false)
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (!isSaving) {
      setName(teamName)
      setIsEditing(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value.length <= 50) {
      setName(value)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsSaving(true)
  }

  if (!canEdit) {
    return (
      <SContainer className={className}>
        <STeamName className={className} title={teamName}>
          {teamName}
        </STeamName>
      </SContainer>
    )
  }

  if (isEditing) {
    return (
      <SEditingContainer className={className}>
        <SInput
          ref={inputRef}
          value={name}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleCancel}
          maxLength={50}
          disabled={isLoading}
        />

        <SSaveButton
          type="text"
          icon={<CheckOutlined />}
          onClick={handleSave}
          onMouseDown={handleMouseDown}
          loading={isLoading}
          disabled={!name.trim() || name.trim() === teamName}
        />
      </SEditingContainer>
    )
  }

  return (
    <SContainer className={className}>
      <STeamName title={teamName}>
        {teamName}
      </STeamName>

      <SEditButton
        type="text"
        icon={<EditOutlined />}
        onClick={handleEditClick}
        size="small"
        color="primary"
      />
    </SContainer>
  )
}

const SEditButton = styled(Button)`
  opacity: 0;
  transition: opacity 0.2s;
  padding: 2px;
  height: auto;
  min-width: auto;
  color: var(--color-gray-600);

  &:hover {
    color: var(--color-primary) !important;
    background-color: rgba(0, 0, 0, 0.04);
  }
`

const SContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  max-width: 100%;
  max-width: calc(var(--sidebar-width, 100%) - 32px - 32px);

  &:hover ${SEditButton} {
    opacity: 1;
  }
`

const STeamName = styled.h1`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  user-select: all;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(var(--sidebar-width, 100%) - 32px - 8px);
`

const SEditingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  width: calc(var(--sidebar-width, 100%) - 32px - 8px);
`

const SInput = styled(Input)`
  font-size: 14px;
  font-weight: 600;
  padding: 2px 8px;
  height: 28px;
  border-radius: 4px;
  max-width: calc(var(--sidebar-width, 100%) - 32px - 8px);
`

const SSaveButton = styled(Button)`
  padding: 2px;
  height: auto;
  min-width: auto;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`

export default EditableTeamName
